import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  model,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { select } from "@ngxs/store";
import {
  GetPartnerInvitationAgreementPartnership,
  UserGroup,
} from "@platform-app/app/core/api/models";
import { PartnersService } from "@platform-app/app/core/api/services";
import { AuthState } from "@platform-app/app/core/auth/auth.state";
import { PendingPartnershipMessageComponent } from "@platform-app/app/main/dashboard-page/introduction-dialog/pending-partnership-message/pending-partnership-message.component";
import { LoaderComponent } from "@platform-app/app/shared/common/components/loader/loader.component";
import { DialogModule } from "primeng/dialog";
import { tap } from "rxjs";

interface IntroductionItemModel {
  title: string;
  subtitle?: string;
  icon: string;
  route: string[];
  queryParams?: { [key: string]: string | boolean };
}

const SCHOOL_INTRODUCTION_ITEMS: IntroductionItemModel[] = [
  {
    title: "Complete Your Organization’s Profile",
    icon: "clinical_notes",
    route: ["/organization-settings", "general-information"],
  },
  {
    title: "Invite Your Clinical Partners",
    subtitle: "to be managed on Externi",
    icon: "domain_add",
    route: ["/partners"],
    queryParams: { invite: true },
  },
  {
    title: "Invite Your Students",
    subtitle: "to create their professional profiles on Externi",
    icon: "school",
    route: ["/students"],
    queryParams: { create: true },
  },
];

const CLINIC_INTRODUCTION_ITEMS: IntroductionItemModel[] = [
  {
    title: "Complete Your Organization’s Profile",
    icon: "clinical_notes",
    route: ["/organization-settings", "general-information"],
  },
  {
    title: "Create Opportunities",
    icon: "school",
    route: ["/our-opportunities"],
    queryParams: { create: true },
  },
  {
    title: "Invite Your Educational Partners",
    subtitle: "to be managed on Externi",
    icon: "domain_add",
    route: ["/partners"],
    queryParams: { invite: true },
  },
];

@Component({
  selector: "app-introduction-dialog",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DialogModule, PendingPartnershipMessageComponent, LoaderComponent],
  templateUrl: "./introduction-dialog.component.html",
  styleUrl: "./introduction-dialog.component.scss",
})
export class IntroductionDialogComponent {
  private readonly router = inject(Router);
  private readonly partnersService = inject(PartnersService);
  private readonly destroyRef = inject(DestroyRef);

  visible = model.required<boolean>();

  isSchoolUser = computed(() => this.roleGroup() === UserGroup.SchoolUser);

  items = computed<IntroductionItemModel[]>(() =>
    this.isSchoolUser() ? SCHOOL_INTRODUCTION_ITEMS : CLINIC_INTRODUCTION_ITEMS,
  );

  loaded = computed(
    () => this.isSchoolUser() || this.pendingPartnership() !== undefined,
  );

  pendingPartnership = signal<
    GetPartnerInvitationAgreementPartnership | null | undefined
  >(undefined);

  private roleGroup = select(AuthState.roleGroup);

  constructor() {
    const effectRef = effect(
      () => {
        if (!this.isSchoolUser()) {
          this.loadPendingPartnership();
          effectRef.destroy();
        }
      },
      { allowSignalWrites: true, manualCleanup: true },
    );
  }

  navigateToAgreement(): void {
    const partnership = this.pendingPartnership();
    if (!partnership) return;

    this.router
      .navigate(["/partners", partnership.partnershipId], {
        fragment: `agreement-${partnership.agreement.id}`,
      })
      .then(() => this.visible.set(false));
  }

  protected navigateToItem(item: IntroductionItemModel) {
    this.router
      .navigate(item.route, { queryParams: item.queryParams })
      .then(() => this.visible.set(false));
  }

  private loadPendingPartnership(): void {
    this.partnersService
      .getPartnerInvitationAgreement()
      .pipe(
        tap((response) => this.pendingPartnership.set(response.partnership)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
